import * as React from 'react';
import { AlertTriangle, CheckCircle2, Info, OctagonAlert, type LucideIcon } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import styles from './Callout.module.css';

export type CalloutTone = 'muted' | 'info' | 'warning' | 'danger' | 'success';

export interface CalloutProps {
  tone?: CalloutTone;
  title?: React.ReactNode;
  children?: React.ReactNode;
  Icon?: LucideIcon | null;
  action?: React.ReactNode;
  className?: string;
}

const TONE_ICON: Record<CalloutTone, LucideIcon | null> = {
  muted: null,
  info: Info,
  warning: AlertTriangle,
  danger: OctagonAlert,
  success: CheckCircle2,
};

/**
 * Плашка-пояснение внутри экрана: «роль теряет доступ к этой странице»,
 * «форма подставлена из шаблона», «синхронизация не удалась».
 *
 * До 12.10.2026 у каждого раздела была своя: `.callout` в ролях и спринтах,
 * `.calloutMuted` в общем словаре админки, `.notice` в журнале ошибок,
 * `.statusError`/`.statusHint` в вики, `.warnPlate`/`.boundPlate` в
 * конструкторе форм — пять радиусов, четыре рецепта заливки и два красных.
 *
 * Тон красит заливку (на 12–16% от поверхности) и значок; текст остаётся
 * `--fg`/`--muted-fg` — читаемость не зависит от тона. Тени нет: плашка лежит
 * внутри блока (закон 11), отделяется заливкой.
 *
 * Не путать с `features/doc-viewer` Callout — это оформление статьи вики
 * (водяной знак, отступы текста), а не сообщение интерфейса.
 *
 * @use пояснение или предупреждение внутри экрана: что произойдёт, почему недоступно, что пошло не так — вместо своей плашки `.callout`/`.notice`.
 */
export const Callout: React.FC<CalloutProps> = ({ tone = 'muted', title, children, Icon, action, className }) => {
  const Glyph = Icon === undefined ? TONE_ICON[tone] : Icon;
  return (
    <div role="note" className={cn(styles.root, styles[tone], !Glyph && styles.noIcon, className)}>
      {Glyph && <Glyph size={16} className={styles.icon} aria-hidden />}
      <div className={styles.body}>
        {title && <p className={styles.title}>{title}</p>}
        {children && <div className={styles.text}>{children}</div>}
        {action && <div className={styles.actions}>{action}</div>}
      </div>
    </div>
  );
};
